import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CREDIT_OPTIONS = [0, 5, 10, 15, 20, 50];
const TRIAL_DAYS_OPTIONS = [0, 3, 7, 14];
const DISCOUNT_OPTIONS = [0, 10, 20];

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    partner_id: '',
    name: '',
    welcome_credits: 0,
    premium_trial_days: 0,
    first_reading_free: false,
    discount_percent: 0,
    discount_code: '',
    custom_message: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [campaignsRes, partnersRes] = await Promise.all([
        axios.get(`${API_URL}/api/growth-hub/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/growth-hub/partners`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setPartners(partnersRes.data.partners || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.partner_id) {
      alert('Lütfen bir partner seçin');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      
      if (editingCampaign) {
        await axios.put(
          `${API_URL}/api/growth-hub/campaigns/${editingCampaign.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/growth-hub/campaigns`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowModal(false);
      setEditingCampaign(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save campaign:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      partner_id: '',
      name: '',
      welcome_credits: 0,
      premium_trial_days: 0,
      first_reading_free: false,
      discount_percent: 0,
      discount_code: '',
      custom_message: ''
    });
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      partner_id: campaign.partner_id,
      name: campaign.name,
      welcome_credits: campaign.welcome_credits || 0,
      premium_trial_days: campaign.premium_trial_days || 0,
      first_reading_free: campaign.first_reading_free || false,
      discount_percent: campaign.discount_percent || 0,
      discount_code: campaign.discount_code || '',
      custom_message: campaign.custom_message || ''
    });
    setShowModal(true);
  };

  const handleToggleActive = async (campaign) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/growth-hub/campaigns/${campaign.id}`,
        { is_active: !campaign.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      console.error('Failed to toggle campaign:', error);
    }
  };

  const getPartnerName = (partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    return partner ? partner.name : 'Bilinmeyen';
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>Yükleniyor...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px' }}>🎯 Kampanyalar ({campaigns.length})</h2>
          <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
            Partner bazlı kampanya ve teklifler
          </p>
        </div>
        <button
          onClick={() => {
            setEditingCampaign(null);
            resetForm();
            setShowModal(true);
          }}
          disabled={partners.length === 0}
          style={{
            background: partners.length === 0 ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '10px',
            cursor: partners.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: partners.length === 0 ? 0.5 : 1
          }}
        >
          <span>+</span>
          <span>Yeni Kampanya</span>
        </button>
      </div>

      {partners.length === 0 && (
        <div style={{
          background: 'rgba(245,158,11,0.1)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          color: '#fbbf24',
          fontSize: '14px'
        }}>
          ⚠️ Kampanya oluşturmak için önce bir partner eklemelisiniz.
        </div>
      )}

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div style={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '20px',
          padding: '64px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '56px', marginBottom: '20px' }}>🎯</div>
          <h3 style={{ margin: '0 0 10px', color: 'white', fontSize: '18px', fontWeight: '600' }}>Henüz kampanya yok</h3>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
            İlk kampanyanızı oluşturarak kullanıcı kazanımına başlayın
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))', gap: '20px' }}>
          {campaigns.map((campaign) => {
            const conversionRate = campaign.total_views > 0 
              ? ((campaign.total_signups / campaign.total_views) * 100).toFixed(1) 
              : 0;
            return (
            <motion.div
              key={campaign.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              style={{
                background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
                border: `1px solid ${campaign.is_active ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: '20px',
                padding: '24px',
                position: 'relative',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Status Badge */}
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: campaign.is_active ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                color: campaign.is_active ? '#22c55e' : '#ef4444',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                border: `1px solid ${campaign.is_active ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`
              }}>
                <span style={{ 
                  width: '6px', 
                  height: '6px', 
                  borderRadius: '50%', 
                  background: campaign.is_active ? '#22c55e' : '#ef4444',
                  boxShadow: campaign.is_active ? '0 0 8px #22c55e' : 'none'
                }}></span>
                {campaign.is_active ? 'Aktif' : 'Pasif'}
              </div>

              {/* Campaign Header */}
              <div style={{ marginBottom: '20px', paddingRight: '80px' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: 'white', fontWeight: '700' }}>{campaign.name}</h3>
                <span style={{
                  background: 'rgba(139,92,246,0.15)',
                  color: '#c084fc',
                  padding: '4px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  {getPartnerName(campaign.partner_id)}
                </span>
              </div>
              
              {/* Premium Benefits Grid */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '10px',
                marginBottom: '20px'
              }}>
                {campaign.welcome_credits > 0 && (
                  <div style={{ 
                    background: 'rgba(139,92,246,0.1)', 
                    borderRadius: '10px', 
                    padding: '12px',
                    border: '1px solid rgba(139,92,246,0.15)'
                  }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hediye Kredi</div>
                    <div style={{ fontSize: '18px', color: '#c084fc', fontWeight: '700' }}>{campaign.welcome_credits} 💎</div>
                  </div>
                )}
                {campaign.premium_trial_days > 0 && (
                  <div style={{ 
                    background: 'rgba(245,158,11,0.1)', 
                    borderRadius: '10px', 
                    padding: '12px',
                    border: '1px solid rgba(245,158,11,0.15)'
                  }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Premium Deneme</div>
                    <div style={{ fontSize: '18px', color: '#fbbf24', fontWeight: '700' }}>{campaign.premium_trial_days} Gün</div>
                  </div>
                )}
                {campaign.first_reading_free && (
                  <div style={{ 
                    background: 'rgba(6,182,212,0.1)', 
                    borderRadius: '10px', 
                    padding: '12px',
                    border: '1px solid rgba(6,182,212,0.15)'
                  }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bonus</div>
                    <div style={{ fontSize: '14px', color: '#22d3ee', fontWeight: '600' }}>İlk Fal Ücretsiz 🔮</div>
                  </div>
                )}
                {campaign.discount_percent > 0 && (
                  <div style={{ 
                    background: 'rgba(34,197,94,0.1)', 
                    borderRadius: '10px', 
                    padding: '12px',
                    border: '1px solid rgba(34,197,94,0.15)'
                  }}>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>İndirim</div>
                    <div style={{ fontSize: '18px', color: '#22c55e', fontWeight: '700' }}>%{campaign.discount_percent}</div>
                  </div>
                )}
              </div>

              {/* Premium Stats Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                padding: '16px',
                background: 'rgba(0,0,0,0.25)',
                borderRadius: '12px',
                marginBottom: '16px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Görüntülenme</div>
                  <div style={{ fontSize: '20px', color: '#8b5cf6', fontWeight: '700' }}>{campaign.total_views || 0}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Kayıt</div>
                  <div style={{ fontSize: '20px', color: '#22c55e', fontWeight: '700' }}>{campaign.total_signups || 0}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dönüşüm</div>
                  <div style={{ fontSize: '20px', color: '#f472b6', fontWeight: '700' }}>{conversionRate}%</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gelir</div>
                  <div style={{ fontSize: '20px', color: '#fbbf24', fontWeight: '700' }}>{campaign.total_revenue || 0}₺</div>
                </div>
              </div>

              {/* Premium Actions */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => handleToggleActive(campaign)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: campaign.is_active 
                      ? 'rgba(239, 68, 68, 0.1)' 
                      : 'rgba(34, 197, 94, 0.1)',
                    border: `1px solid ${campaign.is_active ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)'}`,
                    borderRadius: '10px',
                    color: campaign.is_active ? '#ef4444' : '#22c55e',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {campaign.is_active ? 'Durdur' : 'Aktifleştir'}
                </button>
                <button
                  onClick={() => handleEdit(campaign)}
                  style={{
                    padding: '12px 20px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                    color: 'rgba(255,255,255,0.8)',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Düzenle
                </button>
              </div>
            </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.95)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '20px'
            }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
                border: '1px solid rgba(139,92,246,0.3)',
                borderRadius: '20px',
                padding: '32px',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
            >
              <h2 style={{ margin: '0 0 24px', fontSize: '20px', color: 'white' }}>
                {editingCampaign ? '✏️ Kampanya Düzenle' : '➕ Yeni Kampanya'}
              </h2>

              <form onSubmit={handleSubmit}>
                {/* Partner Selection */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    Partner *
                  </label>
                  <select
                    value={formData.partner_id}
                    onChange={(e) => setFormData({ ...formData, partner_id: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Partner seçin...</option>
                    {partners.filter(p => p.status === 'active').map(partner => (
                      <option key={partner.id} value={partner.id}>{partner.name}</option>
                    ))}
                  </select>
                </div>

                {/* Campaign Name */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    Kampanya Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Örn: Hoşgeldin Kampanyası"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Welcome Credits */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    🎁 Hoşgeldin Kredisi
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {CREDIT_OPTIONS.map(credits => (
                      <button
                        key={credits}
                        type="button"
                        onClick={() => setFormData({ ...formData, welcome_credits: credits })}
                        style={{
                          padding: '10px 16px',
                          background: formData.welcome_credits === credits 
                            ? 'linear-gradient(135deg, rgba(139,92,246,0.4), rgba(99,102,241,0.4))'
                            : 'rgba(255,255,255,0.05)',
                          border: formData.welcome_credits === credits 
                            ? '2px solid #8b5cf6'
                            : '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '10px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        {credits === 0 ? 'Yok' : `${credits} 💎`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Premium Trial */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    ⭐ Premium Deneme
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {TRIAL_DAYS_OPTIONS.map(days => (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setFormData({ ...formData, premium_trial_days: days })}
                        style={{
                          padding: '10px 16px',
                          background: formData.premium_trial_days === days 
                            ? 'linear-gradient(135deg, rgba(139,92,246,0.4), rgba(99,102,241,0.4))'
                            : 'rgba(255,255,255,0.05)',
                          border: formData.premium_trial_days === days 
                            ? '2px solid #8b5cf6'
                            : '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '10px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        {days === 0 ? 'Yok' : `${days} Gün`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* First Reading Free */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '10px'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.first_reading_free}
                      onChange={(e) => setFormData({ ...formData, first_reading_free: e.target.checked })}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                      🔮 İlk Fal Ücretsiz
                    </span>
                  </label>
                </div>

                {/* Discount */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    💰 İndirim
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {DISCOUNT_OPTIONS.map(discount => (
                      <button
                        key={discount}
                        type="button"
                        onClick={() => setFormData({ ...formData, discount_percent: discount })}
                        style={{
                          padding: '10px 16px',
                          background: formData.discount_percent === discount 
                            ? 'linear-gradient(135deg, rgba(139,92,246,0.4), rgba(99,102,241,0.4))'
                            : 'rgba(255,255,255,0.05)',
                          border: formData.discount_percent === discount 
                            ? '2px solid #8b5cf6'
                            : '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '10px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        {discount === 0 ? 'Yok' : `%${discount}`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Message */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    💬 Özel Mesaj (Landing Page görünür)
                  </label>
                  <textarea
                    value={formData.custom_message}
                    onChange={(e) => setFormData({ ...formData, custom_message: e.target.value })}
                    placeholder="Örn: Cafe Latte misafirlerine özel hoş geldin hediyesi"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '14px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    {editingCampaign ? 'Güncelle' : 'Oluştur'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CampaignsPage;
